import * as React from 'react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import BaggageCard from '../../BaggageCard';
import { useTheme } from '../../../theme';
import { getOverweightMinPrice } from '../../../Checkout/store/order/selectors';
import { PassengerLabels } from '../../../Checkout/types';
import { Baggage } from '../../../BaggageService/types';
import { SelectedUpgrade } from '../../IncludedBaggage/types';
import { BaggageType } from '@websky/graphql';
import UpgradeBaggageDialog from '../../UpgradeBaggageDialog/components/UpgradeBaggageDialog';
import { useBaggageToggleable } from '../../hooks';
import { useSelectedServicesBySegments } from '../../UpgradeBaggageDialog/hooks';
import HeaderEquipmentItem from '../../UpgradeBaggageDialog/components/BaggageDialogHeader/HeaderEquipmentItem/HeaderEquipmentItem';
import { Passenger, Segment, SelectedBaggageService } from '../../types';
import { BaggageContext } from '../../context';
import BaggageDirection from '../../BaggageList/components/BaggageDirection/BaggageDirection';
import { ServiceMeta } from '../../../Checkout/store/selectedServices/actions';
import { CdnIcon } from '@shared/ui';
import BaggageDeleteButton, { hasDeletableServices } from '../../BaggageDeleteButton';

interface Props {
	className?: string;
	passengers: Passenger[];
	passengerLabels: PassengerLabels;
	segments: Segment[];
	services: Baggage[];
	selectedServices: SelectedUpgrade[];
	isLoading?: boolean;
	disabled?: boolean;
	dynamicBaggagePricing?: boolean;
	analyticsMeta?: ServiceMeta;
	onDelete?: (servicesToDelete: SelectedBaggageService[]) => void;
}

const OverweightBaggage: React.FC<Props> = ({
	className,
	passengers,
	passengerLabels,
	segments,
	services,
	selectedServices,
	isLoading,
	disabled,
	dynamicBaggagePricing,
	analyticsMeta,
	onDelete
}) => {
	const { OverweightBaggage: css } = useTheme('OverweightBaggage');
	const { t } = useTranslation('AdditionalBaggage');

	const { isOpen, open, close } = useBaggageToggleable(services);

	const isReadOnly = useContext(BaggageContext)?.isReadOnly;
	const overweightMinPrice = useSelector(getOverweightMinPrice);

	const { selectedItems, hasSelected, totalPrice } = useSelectedServicesBySegments(
		services,
		segments,
		selectedServices
	);

	return (
		<>
			<BaggageCard
				className={className}
				classNameIcon={css.icon}
				icon={<CdnIcon icon={'Baggage/Overweight'} />}
				title={t('Overweight')}
				description={t('Need to add another 10-30 kg to your total checked baggage?')}
				fromPrice={overweightMinPrice}
				totalPrice={totalPrice}
				hasSelected={hasSelected}
				onEdit={open}
				onAdd={open}
				readOnly={isReadOnly}
			>
				{hasSelected && (
					<div className={css.flights}>
						{selectedItems.map((selectedService, index) => (
							<div className={css.flight} key={index}>
								<HeaderEquipmentItem
									className={css.flight_icon}
									icon={<CdnIcon icon={'Baggage/Overweight'} />}
									isActive
								/>
								<div className={css.flight_count}>x{selectedService.count}</div>
								<span className={css.flight_info}>
									{selectedService.service.value
										? `${t('Aircraft:Up to')} ${selectedService.service.value.amount} ${t(
												`BaggageService:${selectedService.service.value.measurement}`
										  )}`
										: ''}
									,{' '}
									<BaggageDirection
										className={css.flight_direction}
										directions={selectedService.directions}
										allSegments={selectedService.allSegments}
									/>
								</span>
								{onDelete && hasDeletableServices(selectedService.selectedServices) && (
									<BaggageDeleteButton
										className={css.flight_deleteButton}
										onClick={() => onDelete(selectedService.selectedServices)}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</BaggageCard>

			<UpgradeBaggageDialog
				open={isOpen}
				onClose={close}
				passengers={passengers}
				passengerLabels={passengerLabels}
				segments={segments}
				includedBaggage={[]}
				upgradeBaggage={services}
				selectedServices={selectedServices}
				headerProps={{
					header: t('Overweight'),
					description: t('For check-in baggage.')
				}}
				disabled={isLoading || disabled}
				baggageType={BaggageType.BaggageExcess}
				dynamicBaggagePricing={dynamicBaggagePricing}
				isLoading={isLoading}
				analyticsMeta={analyticsMeta}
			/>
		</>
	);
};

export default OverweightBaggage;
